/*
 * Copyright 2024 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { Box, Button } from "@material-ui/core";
import {
    AppLoadingIndicatorV1,
    useAuthProviderV1,
} from "@startree-ui/platform-ui";
import axios, { AxiosError } from "axios";
import React, {
    createContext,
    FunctionComponent,
    useContext,
    useEffect,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AppError } from "../../app-error/app-error.component";
import { useEnvironmentInfoContext } from "../../environment-info-provider/environment-info-provider.component";
import { getWorkspaces, WorkspaceData } from "../rest/workspace.rest";
import { useWorkspaceProviderStore } from "../store/workspace.store";
import { getRequestInterceptorForWorkspace } from "../utils/workspace.utils";

export interface WorkspaceContextProps {
    updateWorkspace: (workspace: WorkspaceData) => void;
    selectedWorkspace: WorkspaceData;
    workspaceList: WorkspaceData[];
}

export const WorkspaceProvider: FunctionComponent = ({ children }) => {
    const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
    const [axiosRequestInterceptorId, setAxiosRequestInterceptorId] = useState<
        number | null
    >(null);
    const { authenticated, authDisabled, authUser, logout } =
        useAuthProviderV1();
    const authDone = authenticated || authDisabled;
    const [workspaces, setWorkspaces] = useState<WorkspaceData[]>([]);
    const { selectedWorkspace, setSelectedWorkspace } =
        useWorkspaceProviderStore((state) => ({
            selectedWorkspace: state.selectedWorkspace,
            setSelectedWorkspace: state.setSelectedWorkspace,
        }));
    const [isError, setIsError] = useState(false);
    const {
        envFlags: { isSharedSaasEnv },
    } = useEnvironmentInfoContext();
    const { t } = useTranslation();

    useEffect(() => {
        // if not authenticated continue loading the app and let the app handle logout redirect
        // Should not be called for non shared-saas envs
        if (!authDone) {
            setIsPageLoading(false);

            return;
        }

        fetchWorkspaces();
    }, []);

    const fetchWorkspaces = async (): Promise<void> => {
        let newSelectedWorkspace = {} as WorkspaceData;
        let newWorkspaces: WorkspaceData[] = [];

        try {
            newWorkspaces = await getWorkspaces();

            // selectedWorkspace is already present in store
            if (
                selectedWorkspace.workspaceId &&
                newWorkspaces.find(
                    (workspace) =>
                        workspace.workspaceId === selectedWorkspace.workspaceId
                )
            ) {
                newSelectedWorkspace = { ...selectedWorkspace };
            } else {
                newSelectedWorkspace = newWorkspaces[0] || {};
            }
        } catch (error) {
            const axiosError = error as AxiosError;

            // if the error is not 401, then show the error message
            if (axiosError?.response?.status !== 401) {
                setIsError(true);
            }
        } finally {
            setWorkspaces(newWorkspaces);
            setSelectedWorkspace(newSelectedWorkspace);
            newSelectedWorkspace.workspaceId &&
                addWorkspaceHeaderToAxios(newSelectedWorkspace.workspaceId);
            setIsPageLoading(false);
        }
    };

    const addWorkspaceHeaderToAxios = (workspaceId: string): void => {
        if (axiosRequestInterceptorId !== null) {
            axios.interceptors.request.eject(axiosRequestInterceptorId);
        }

        setAxiosRequestInterceptorId(
            axios.interceptors.request.use(
                getRequestInterceptorForWorkspace(workspaceId)
            )
        );
    };

    if (isPageLoading) {
        return <AppLoadingIndicatorV1 />;
    }

    if (isSharedSaasEnv && isError) {
        return (
            <AppError
                details={t(
                    "message.please-try-reloading-the-page-or-contact-startree-support"
                )}
                header={t("message.error-fetching-workspace-details")}
            >
                <Box mt={1}>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => logout()}
                    >
                        {t("label.sign-out")}
                    </Button>
                </Box>
            </AppError>
        );
    }

    if (isSharedSaasEnv && workspaces.length === 0) {
        return (
            <AppError
                details={t(
                    "message.please-try-reloading-the-page-or-contact-startree-support"
                )}
                header={t("message.unable-to-find-any-workspaces-for-user", {
                    user: authUser.email,
                })}
            >
                <Box mt={1}>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => logout()}
                    >
                        {t("label.sign-out")}
                    </Button>
                </Box>
            </AppError>
        );
    }

    return (
        <WorkspaceProviderContext.Provider
            value={{
                selectedWorkspace: selectedWorkspace,
                workspaceList: workspaces,
                updateWorkspace: setSelectedWorkspace,
            }}
        >
            {children}
        </WorkspaceProviderContext.Provider>
    );
};

const WorkspaceProviderContext = createContext<WorkspaceContextProps>(
    {} as WorkspaceContextProps
);

export const useWorkspaceContext = (): WorkspaceContextProps => {
    return useContext(WorkspaceProviderContext);
};
